/* eslint-disable */
import * as React from 'react';
import dayjs from "utils/dayjs";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, IconButton, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useConfig from 'hooks/useConfig'; // 💡 테마 처리를 위해 추가

// 🎨 다크/라이트 모드 동적 스타일 함수
const getCustomInputStyles = (hasValue, isDark) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: isDark ? '#0f172a' : '#ffffff', // 🎯 라이트모드 하얀색 보정
    paddingRight: hasValue ? '70px !important' : '40px !important', 
    '& fieldset': { borderColor: isDark ? '#334155' : '#cbd5e1' },
    '&:hover fieldset': { borderColor: isDark ? '#475569' : '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
  },
  '& input': {
    color: `${isDark ? '#e2e8f0' : '#000000'} !important`, // 🎯 라이트모드 검은 글자 보정
    textAlign: 'center !important',
    fontSize: '0.85rem !important',
    paddingLeft: hasValue ? '70px !important' : '40px !important', 
    paddingRight: '0px !important',
  }
});

// 코드로 직접 그린 달력 그래픽
const PureCalendarIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path fill="currentColor" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
  </SvgIcon>
);

// 직접 그린 X(초기화) 그래픽
const PureClearIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path 
      fill="currentColor" 
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
    />
  </SvgIcon>
);

export const CustomMuiDatePicker = ({ field, value, name, setValue, fieldState, ...restProps }) => {
  const { t, i18n } = useTranslation();
  const { mode } = useConfig(); // 💡 라이트/다크 감지
  const isDark = mode === 'dark';

  // 💡 1. 동적 로케일 및 표시 포맷 추출 (언어 변경 시 즉시 반영)
  const activeLocale = (i18n.language || process.env.REACT_APP_LANGUAGE || 'ko').toLowerCase();
  const currentFormat = t('message.common.text.date.format.date') || 'YYYY-MM-DD';

  // 💡 2. field.value 또는 value 지원
  const rawValue = field?.value || value;
  const hasValue = !!(rawValue && String(rawValue).trim());

  // 💡 3. safeValue 보정 (다양한 구분자/포맷 예외 처리로 파란 원 하이라이트 보장)
  const safeValue = React.useMemo(() => {
    if (!hasValue) return null;
    let d = dayjs(rawValue);
    if (!d.isValid()) {
      d = dayjs(rawValue, currentFormat);
    }
    return d.isValid() ? d : null;
  }, [rawValue, currentFormat, hasValue]);

  const [open, setOpen] = React.useState(false);

  // 값 삭제/초기화 공통 처리
  const handleClear = (e) => {
    if (e) e.stopPropagation();
    if (setValue && name) setValue(name, '', { shouldDirty: true });
    if (field && field.onChange) field.onChange('');
  };

  return (
    <LocalizationProvider 
      dateAdapter={AdapterDayjs} 
      dateLibInstance={dayjs} 
      adapterLocale={activeLocale}
    >
      <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'flex-start' }}>
        <DatePicker
          {...restProps}
          value={safeValue}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(newValue) => {
            const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
            if (setValue && name) setValue(name, formatted, { shouldDirty: true });
            if (field && field.onChange) field.onChange(formatted);
          }}
          inputRef={field ? field.ref : undefined}
          format={currentFormat}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              error: !!fieldState?.error,
              helperText: fieldState?.error?.message,
              sx: getCustomInputStyles(hasValue, isDark), 
              placeholder: currentFormat,
              InputProps: {
                endAdornment: null 
              }
            },
            popper: {
              sx: {
                // 💡 팝업 달력 배경/글자색도 테마 대응
                '& .MuiPaper-root': {
                  bgcolor: isDark ? '#0f172a' : '#ffffff',
                  color: isDark ? '#e2e8f0' : '#000000',
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                },
                '& .MuiPickersDay-root': {
                  color: isDark ? '#e2e8f0' : '#000000',
                  '&.Mui-selected': {
                    bgcolor: '#3b82f6 !important',
                    color: '#ffffff !important'
                  }
                },
                // 일요일 빨간색 스타일
                '& .MuiDayCalendar-weekHeader .MuiTypography-root:first-of-type': { color: '#ef4444 !important' },
                '& .MuiPickersDay-root[aria-colindex="1"]': { color: '#ef4444 !important' }
              }
            }
          }}
        />

        {/* 1. [초기화 X 버튼] */}
        {hasValue && (
          <IconButton 
            size="small" 
            onClick={handleClear}
            sx={{ 
              position: 'absolute', 
              right: '38px', 
              top: '5px',
              zIndex: 10,
              color: isDark ? '#a0aec0 !important' : '#64748b !important', 
              padding: '6px',
              '&:hover': { backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            <PureClearIcon style={{ fontSize: '1.15rem' }} />
          </IconButton>
        )}

        {/* 2. [달력오픈 버튼] */}
        <IconButton 
          size="small" 
          onClick={() => setOpen((prev) => !prev)}
          sx={{ 
            position: 'absolute', 
            right: '8px', 
            top: '5px',
            zIndex: 10,  
            color: isDark ? '#a0aec0 !important' : '#64748b !important', 
            padding: '6px',
            '&:hover': { backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          <PureCalendarIcon style={{ fontSize: '1.15rem' }} />
        </IconButton>

      </Box>
    </LocalizationProvider>
  );
};